<template>
  <div class="medication-details new-globals">
    <div class="detail-body">
      <div class="detail-overview">
        <md-field class="ui-theme-3 detail-field name-field">
          <label>Medication</label>
          <md-input
            :value="group.label.en"
            :disabled="!isCustomGroup"
            @input="name => updateName(name)"
          />
        </md-field>
        <md-field class="ui-theme-3 detail-field description-field">
          <label>Description</label>
          <md-textarea
            :value="dosageForm ? dosageForm.description : medication.description"
            :disabled="readonly"
            :maxlength="500"
            @input="description => updateDescription(description)"
          />
        </md-field>
        <md-field class="ui-theme-3 detail-field dosage-field">
          <label for="dosageformtype">Dosage Form</label>
          <md-select
            id="dosageformtype"
            :value="dosageFormType"
            :disabled="readonly"
            @input="updateDosageFormType"
          >
            <md-option
              v-for="df in dosageForms"
              :key="df.key"
              :value="df.key"
            >
              {{ df.label.en }}
            </md-option>
          </md-select>
        </md-field>
        <concentration
          :dosage-form-type="dosageFormType"
          :concentration="dosageForm ? dosageForm.concentration : medication.defaultConcentration"
          :dosage-form-errors="dosageFormErrors"
          :readonly="readonly"
          @update="updateConcentration"
        />
        <div v-if="medicationEnabled" class="admin-cell">
          <span>
            Administering Unit
            <help-tooltip v-if="administeringUnitError" type="error">
              {{ administeringUnitError.message }}
            </help-tooltip>
          </span>
          <unit-type-selector
            class="administering-units"
            :selected="dosageForm.administeringUnits || []"
            :limit-to="availableAdminUnits"
            :readonly="readonly"
            @update="updateAdministeringUnits"
          />
        </div>
      </div>
      <md-divider class="divider" />
      <div class="md-body-1">{{ routeCaption }}</div>
      <div class="new-button-row">
        <md-button
          v-if="dosageForm"
          class="md-primary md-outlined-button add-route"
          :disabled="readonly"
          @click="showRouteDialog = true"
        >
          <md-icon>add</md-icon>
          Add New Route
        </md-button>
        <help-tooltip v-if="noRouteError" type="error">
          {{ noRouteError.message }}
        </help-tooltip>
      </div>
      <div v-for="(route, idx) of routes" :key="route.key + idx" class="detail-routes">
        <div class="route-row">
          <div class="route-column-left">
            <md-button
              :disabled="readonly"
              class="md-icon-button md-primary md-raised md-dense"
              @click="updateEnabled(route.key, false)"
            >
              <md-icon>remove</md-icon>
            </md-button>
            <span class="route-name">{{ route.name[language] }}</span>
          </div>
          <div v-if="route.key === 'IV_IO_PUSH'" class="route-column-right">
            <div class="iv-io-route-fields">
              <div class="route-row-grow" />
              <div class="route-row-auto">
                <md-field class="ui-theme-3 detail-field push-time-frame">
                  <label>Over Time</label>
                  <md-select
                    v-model="state.selectedTimeFrames[idx]"
                    md-dense
                    multiple
                    :disabled="readonly"
                    @md-selected="timeframes => updatePushTimeFrames(route.key, timeframes)"
                  >
                    <md-option
                      v-for="timeframe of pushTimeFrames"
                      :key="timeframe.key"
                      :value="timeframe.key"
                    >
                      {{ timeframe.label.en }}
                    </md-option>
                  </md-select>
                </md-field>
                <help-tooltip v-if="timeframeError" type="error">
                  {{ timeframeError.message }}
                </help-tooltip>
              </div>
            </div>
          </div>
          <div v-if="route.key === 'IV_IO_INFUSION'" class="route-column-right">
            <div class="iv-io-route-fields">
              <div class="route-row-grow" />
              <div class="route-row-auto">
                <md-field class="ui-theme-3 detail-field">
                  <label>Dosings</label>
                  <infusion-dosing-type-select
                    class="dosing-types"
                    :value="route.options ? route.options.infusionDosingTypes : []"
                    :disabled="readonly"
                    @input="dosingTypeKeys => updateInfusionDosingTypes(route.key, dosingTypeKeys)"
                  />
                </md-field>
                <help-tooltip v-if="dosingError" type="error">
                  {{ dosingError.message }}
                </help-tooltip>
                <md-field class="ui-theme-3 detail-field">
                  <label>Volumes</label>
                  <infusion-bag-volume-select
                    class="bag-volumes"
                    :value="route.options ? route.options.infusionBagVolumes : []"
                    :disabled="readonly"
                    @input="bagVolumeKeys => updateInfusionBagVolumes(route.key, bagVolumeKeys)"
                  />
                </md-field>
                <help-tooltip v-if="bagSizeError" type="error">
                  {{ bagSizeError.message }}
                </help-tooltip>
              </div>
            </div>
            <div class="formulary-toggle-container">
              <md-switch
                class="md-primary ui-theme-3"
                :value="route.options ? route.options.formularyEnabled : false"
                :disabled="readonly"
                @change="enabled => updateFormularyEnabled(route.key, enabled)"
              />
              <span>Allow formulary</span>
            </div>
            <div v-if="route.options && route.options.formularyEnabled" class="formulary-container">
              <div>
                <span class="md-title">Formulary</span>
                <help-tooltip type="info">
                  Formulary will be used for IV pump dosages only
                </help-tooltip>
              </div>
              <div>
                <md-button
                  class="md-primary md-outlined-button add-route"
                  :disabled="readonly || !canAddDosing(route)"
                  @click="addFormulary(route.key)"
                >
                  <md-icon>add</md-icon>
                  Add New Dosing
                </md-button>
              </div>
              <div
                v-for="(formulary, index) in route.options.formularies"
                :key="index"
                class="formulary-form-container"
              >
                <md-button
                  class="md-icon-button md-primary md-raised md-dense"
                  :disabled="readonly"
                  @click="removeFormulary(route.key, index)"
                >
                  <md-icon>remove</md-icon>
                </md-button>
                <formulary-form
                  :formulary="formulary"
                  :volumes="route.options.infusionBagVolumes"
                  :allowable-dosing-types="route.options.infusionDosingTypes"
                  :errors="formularyErrors(index)"
                  :readonly="readonly"
                  @update="formulary => updateFormulary(route.key, index, formulary)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <md-divider />
    <new-med-route-dialog
      v-if="medicationEnabled"
      :show="showRouteDialog"
      :routes="availableRoutes"
      @cancel="cancelNewRoute"
      @submit="submitNewRoute"
    />

    <md-dialog-confirm
      :md-active.sync="showConfirmDialog"
      class="confirm-dialog"
      :md-title="medication.name.en"
      md-content="Are you sure you want to delete this medication?"
      md-confirm-text="Yes"
      md-cancel-text="Cancel"
      @md-cancel="showConfirmDialog = false"
      @md-confirm="deleteMedication"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';

import defaultRoutes from '~lib/common/config/routes';
import { dosageForms, medicationGroups, pushTimeFrames, unitTypes } from '~lib/common/config/medication';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import NewMedRouteDialog from './NewMedRouteDialog';
import Concentration from './Concentration';
import UnitTypeSelector from './UnitTypeSelector';
import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect';
import InfusionDosingTypeSelect from '~lib/common/components/medications/InfusionDosingTypeSelect';
import FormularyForm from '../FormularyForm';

export default {
  name: 'case-medication-details',
  components: {
    HelpTooltip,
    NewMedRouteDialog,
    Concentration,
    UnitTypeSelector,
    InfusionBagVolumeSelect,
    InfusionDosingTypeSelect,
    FormularyForm,
  },
  props: {
    medications: {
      type: Array,
      required: true,
    },
    medication: {
      type: Object,
      required: true,
    },
    dosageForm: {
      type: Object,
      default: () => null,
    },
    customGroups: {
      type: Array,
      default: () => null,
    },
    language: {
      type: String,
      default: 'en',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      routeDataLoaded: false,
      showRouteDialog: false,
      showConfirmDialog: false,
      state: {
        medication: this.medication,
        newRouteType: null,
        selectedTimeFrames: null,
      },
      dosageForms: sortBy(dosageForms, df => df.label.en),
      pushTimeFrames,
    };
  },
  computed: {
    caption() {
      return this.medicationEnabled ? '(make a selection to modify it)' : '(turn on a medication to modify it)';
    },
    routeCaption() {
      return `${this.medicationEnabled ? '' : 'Default'} Routes`;
    },
    medicationEnabled() {
      return this.dosageForm !== null;
    },
    routes() {
      let routes = [];
      if (this.medicationEnabled) {
        const enabledRoutes = this.dosageForm.enabledRoutes || [];
        routes = enabledRoutes.map(route => {
          const defaultRoute = defaultRoutes.find(mr => mr.key === route.key);
          return {
            key: route.key,
            name: defaultRoute.name,
            hasTimeFrames: defaultRoute.key === 'IV_IO_PUSH',
            options: route.options,
          };
        });
      } else if (this.medication) {
        routes = this.medication.routes.filter(route => {
          const medRoute = this.medication.routes.find(r => r.key === route.key);
          return medRoute;
        });
      }
      return sortBy(routes, route => route.name.en);
    },
    availableRoutes() {
      const routes = defaultRoutes.filter(route => {
        return !this.dosageForm.enabledRoutes.find(er => er.key === route.key);
      });
      return sortBy(routes, route => route.name.en);
    },
    isCustomGroup() {
      return (this.customGroups || []).find(cg => cg.key === this.group.key);
    },
    group() {
      return medicationGroups.find(g => g.key === this.medication.group) ||
        (this.customGroups || []).find(g => g.key === this.medication.group);
    },
    dosageFormType() {
      return this.medicationEnabled ? this.dosageForm.dosageFormType : this.medication.dosageForm;
    },
    availableAdminUnits() {
      let available = [];
      const { numeratorUnits } = this.dosageForm.concentration;
      const unitType = unitTypes.find(ut => ut.key === numeratorUnits);
      if (unitType) {
        // Get the unit types of the same category, and exclude cc, which is deprecated
        const numeratorUnitTypes = unitTypes.filter(
          ut => ut.category === unitType.category && ut.key !== 'VOLUME_CC'
        );
        available = available.concat(numeratorUnitTypes.map(ut => ut.key));
      }
      const dfData = dosageForms.find(df => df.key === this.dosageForm.dosageFormType);
      if (dfData.unitType2) {
        available.push(dfData.unitType2);
      } else if (this.dosageForm.concentration.denominatorUnits) {
        available.push(this.dosageForm.concentration.denominatorUnits);
      }
      return available;
    },
    dosageFormErrors() {
      let errors = []
      if (this.$store && this.dosageForm) {
        errors = get(this.$store.getters['validation/errorsByFieldAndId'], `dosageForms.${this.dosageForm.id}`, []);
      }
      return errors;
    },
    noRouteError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'no-routes');
    },
    administeringUnitError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'administeringUnit');
    },
    timeframeError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'pushTimeframes');
    },
    dosingError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'infusionDosingTypes');
    },
    bagSizeError() {
      return this.dosageFormErrors.find(e => e.metadata.propertyName === 'infusionBagVolumes');
    },
  },
  watch: {
    medication(medication) {
      if (!medication) {
        return;
      }
      if (!this.state.medication || this.state.medication.id !== medication.id) {
        this.state.medication = medication;
      }
    },
    dosageForm() {
      this.loadRouteData();
    },
  },
  created() {
    this.emitUpdate = debounce(this.emitUpdate, 3000);
    this.loadRouteData();
  },
  methods: {
    loadRouteData() {
      this.routeDataLoaded = false;
      this.state.selectedTimeFrames = this.routes.map(route => {
        if (route.hasTimeFrames) {
          if (route.options && route.options.pushTimeframes) {
            return route.options.pushTimeframes;
          } else {
            return [];
          }
        }
        return null; // don't show dropdown
      });
      this.$nextTick(() => {
        this.routeDataLoaded = true;
      });
    },
    cancelNewRoute() {
      this.showRouteDialog = false;
    },
    submitNewRoute(key) {
      this.updateEnabled(key, true);
      this.showRouteDialog = false;
    },
    updateName(name) {
      if (!this.routeDataLoaded) {
        return;
      }
      const newGroup = {
        ...this.group,
        label: {
          en: name
        }
      };
      const updatedCustomGroups = this.customGroups || [];
      const groupIndex = updatedCustomGroups.findIndex(g => g.key === this.group.key);
      if (groupIndex >= 0) {
        updatedCustomGroups.splice(groupIndex, 1, newGroup);
        this.$emit('updateCase', { customMedGroups: updatedCustomGroups });
      }
    },
    updateDescription(value) {
      const newDosageForm = {
        ...this.dosageForm,
        description: value,
      };
      this.emitDosageFormUpdate(newDosageForm);
    },
    updateDosageFormType(value) {
      const newDosageForm = {
        ...this.dosageForm,
        dosageFormType: value,
        administeringUnits: null,
      };
      this.emitDosageFormUpdate(newDosageForm);
    },
    updateConcentration(updated, unitsChanged) {
      if (!this.routeDataLoaded || !this.dosageForm) {
        return;
      }
      const administeringUnits = unitsChanged ? null : this.dosageForm.administeringUnits;
      const newDosageForm = {
        ...this.dosageForm,
        concentration: updated,
        administeringUnits
      };
      this.emitDosageFormUpdate(newDosageForm);
    },
    updateAdministeringUnits(units) {
      if (!this.routeDataLoaded) {
        return;
      }
      const newDosageForm = {
        ...this.dosageForm,
        administeringUnits: units
      };
      this.emitDosageFormUpdate(newDosageForm);
    },
    updatePushTimeFrames(routeKey, timeframes) {
      if (!this.routeDataLoaded) {
        return;
      }
      const updatedRoutes = this.dosageForm.enabledRoutes;
      const route = updatedRoutes.find(route => route.key === routeKey);
      if (route) {
        route.options.pushTimeframes = timeframes;
        const newDosageForm = {
          ...this.dosageForm,
          enabledRoutes: updatedRoutes
        };
        this.emitDosageFormUpdate(newDosageForm);
      }
    },
    updateInfusionBagVolumes(routeKey, bagVolumeKeys) {
      if (!this.routeDataLoaded || !this.dosageForm) {
        return;
      }
      const updatedRoutes = this.dosageForm.enabledRoutes;
      const route = updatedRoutes.find(route => route.key === routeKey);
      if (route) {
        route.options.infusionBagVolumes = bagVolumeKeys;
        const newDosageForm = {
          ...this.dosageForm,
          enabledRoutes: updatedRoutes
        };
        this.emitDosageFormUpdate(newDosageForm);
      }
    },
    updateInfusionDosingTypes(routeKey, dosingTypeKeys) {
      if (!this.routeDataLoaded || !this.dosageForm) {
        return;
      }
      const updatedRoutes = this.dosageForm.enabledRoutes;
      const route = updatedRoutes.find(route => route.key === routeKey);
      if (route) {
        route.options.infusionDosingTypes = dosingTypeKeys;
        const newDosageForm = {
          ...this.dosageForm,
          enabledRoutes: updatedRoutes
        };
        this.emitDosageFormUpdate(newDosageForm);
      }
    },
    updateEnabled(key, value) {
      let enabledRoutes = this.dosageForm.enabledRoutes || [];
      if (value) {
        enabledRoutes.push({
          key,
          options: {
          }
        });
      } else {
        enabledRoutes = enabledRoutes.filter(er => er.key !== key);
      }
      const newDosageForm = {
        ...this.dosageForm,
        enabledRoutes
      };
      this.emitDosageFormUpdate(newDosageForm);
    },
    emitUpdate(medication) {
      if (!this.routeDataLoaded) {
        return;
      }
      const payload = {
        id: medication.id,
        name: medication.name,
        description: medication.description,
      }
      if (medication.isCustom) {
        console.log('CaseMedicationDetails save goes here')
      } else {
        this.$emit(medication.id ? 'save' : 'add', payload);
      }
    },
    emitDosageFormUpdate(updatedDosageForm) {
      if (!this.routeDataLoaded) {
        return;
      }
      this.$emit('updateDosageForm', updatedDosageForm);
    },
    deleteMedication() {
      this.showConfirmDialog = false;
      this.$emit('remove', this.medication);
    },
    addFormulary(routeKey) {
      this.updateRoute(routeKey, route => {
        route.options.formularies = (route.options.formularies || []).concat({
          dose: 1,
          dosing: route.options.infusionDosingTypes[0],
          volume: get(route.options, 'infusionBagVolumes[0]', null),
        });
      });
    },
    removeFormulary(routeKey, formularyIndex) {
      this.updateRoute(routeKey, route => {
        route.options.formularies.splice(formularyIndex, 1);
      });
    },
    updateFormulary(routeKey, formularyIndex, formulary) {
      this.updateRoute(routeKey, route => {
        route.options.formularies[formularyIndex] = formulary;
      });
    },
    updateFormularyEnabled(routeKey, value) {
      this.updateRoute(routeKey, route => {
        route.options.formularyEnabled = value;
      });
    },
    updateRoute(routeKey, callback) {
      const updatedRoutes = this.dosageForm.enabledRoutes;
      const route = updatedRoutes.find(route => route.key === routeKey);
      if (route) {
        callback(route)
        const newDosageForm = {
          ...this.dosageForm,
          enabledRoutes: updatedRoutes
        };
        this.emitDosageFormUpdate(newDosageForm);
      }
    },
    canAddDosing(route) {
      return route.options.infusionDosingTypes && route.options.infusionDosingTypes.length > 0 &&
             route.options.infusionBagVolumes && route.options.infusionBagVolumes.length > 0;
    },
    formularyErrors(index) {
      return this.dosageFormErrors.filter(e => e.metadata.propertyName && e.metadata.propertyName.startsWith(`formulary[${index}]`))
    },
  },
}
</script>

<style scoped>
.medication-details {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.detail-overview {
  flex: 1;
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem;
}

.detail-field {
  margin: 0 !important;
}

.detail-field + .detail-field {
  margin-left: 8px !important;
}

.detail-field-nolabel {
  margin: 0 !important;
  padding-top: 6px !important;
  padding-bottom: 0 !important;
  min-height: 40px !important;
}

.description-field {
  grid-row-start: span 2;
}

.divider {
  margin: 1rem 0;
}

.details-routes {
  display: flex;
  flex-direction: column;
}

.new-button-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.route-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 48px;
  margin-bottom: 8px;
}

.route-column-left {
  display: flex;
  align-items: center;
}

.route-column-right {
  display: flex;
  flex-direction: column;
  justify-items: center;
  flex: 1;
}

.iv-io-route-fields {
  display: flex;
}
.route-row-grow {
  flex: 1;
}

.route-row-auto {
  display: flex;
  flex: 0;
}

.route-name {
  padding-left: 0.5rem;
}

.iv-io-route-top {
  display: flex;
}

.medication-actions {
  display: flex;
  justify-content: flex-end;
}

.md-button.add-route {
  margin-left: 0px;
  margin-bottom: 1rem;
}

.add-route-dialog {
  height: auto;
}

.admin-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-cell span {
  flex: 1;
  padding-right: 0.5rem;
}

.admin-cell .unit-type-selector {
  flex: 1;
}

.formulary-toggle-container {
  display: flex;
  align-items: center;
}

.formulary-container {
  margin-top: 8px;
  padding: 8px;
  background-color: #f3f3f3;
  border-radius: 6px;
  border: 1px solid #8d8d8d;
}

.formulary-form-container {
  display: flex;
  align-items: center;
  padding-top: 4px;
  padding-bottom: 4px;
}

span.md-title {
  font-size: 16px;
}
</style>